import { offset, shift } from '@floating-ui/react-dom';
import { cx } from '@linaria/core';
import { useRef, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { resets } from '@ui/css';
import { Overlay, TransitionSafeFocusOn, overlayTransitionDurationMs, CloseButton } from '@ui/kit';
import { SizeTransition } from '@utils/react';
import { useDialoguesQuery } from './api.get-dialogues.query.js';
import { DialogueEntry } from './atom.dialogue-entry.js';
import { DialogueInput } from './atom.input.js';
import { overlay, container, header, heading, closeButton, messagesWrapper, messagesContainer, loadingTransitionDurationMs, loading, sizes } from './overlay.dialog.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function DialogOverlay(props) {
  return /*#__PURE__*/jsx(Overlay, {
    className: overlay,
    active: props.active && !!props.dialogId,
    containerRef: props.pointAt,
    placement: "bottom-end",
    middlewares: [offset(0), shift({
      padding: 30,
      crossAxis: true
    })],
    withArrow: false,
    children: /*#__PURE__*/jsx(DialogContent
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    , {
      dialogId: props.dialogId,
      componentTitle: props.componentTitle,
      onClose: props.onClose
    })
  });
}
function DialogContent(props) {
  const dialogues = useDialoguesQuery(props.dialogId);
  const messagesWrapperRef = useRef(null);
  const sortedDialogues = [...(dialogues.data ?? [])].sort((a, b) => {
    if (a.type === "message" && a.state === "failed" && (b.type !== "message" || b.state !== "failed")) {
      return 1;
    }
    if (b.type === "message" && b.state === "failed" && (a.type !== "message" || a.state !== "failed")) {
      return -1;
    }
    return a.timestamp.getTime() - b.timestamp.getTime();
  });
  const messagesKey = dialogues.data?.map(it => it.id).join(",");

  // Ensures that we scroll to the bottom automatically each time a new message
  // is submitted.
  useEffect(() => {
    const container = messagesWrapperRef.current;
    if (!container) {
      return;
    }
    container.scrollTop = container.scrollHeight - container.clientHeight;
  }, [messagesWrapperRef, messagesKey]);
  return /*#__PURE__*/jsx(TransitionSafeFocusOn, {
    transitionEnterDuration: overlayTransitionDurationMs,
    onEscapeKey: props.onClose,
    onClickOutside: props.onClose,
    scrollLock: true,
    focusLock: true,
    allowPinchZoom: true,
    autoFocus: true,
    returnFocus: {
      preventScroll: true
    },
    preventScrollOnFocus: true,
    children: /*#__PURE__*/jsxs("dialog", {
      open: true,
      className: container,
      children: [/*#__PURE__*/jsxs("header", {
        className: header,
        children: [/*#__PURE__*/jsx("h1", {
          className: resets.screenReaderOnlyCls,
          children: "Dialogspor"
        }), /*#__PURE__*/jsx("h2", {
          className: heading,
          children: props.componentTitle
        }), /*#__PURE__*/jsx(CloseButton, {
          label: "Luk dialogspor",
          className: closeButton,
          onClose: props.onClose
        })]
      }), /*#__PURE__*/jsx("div", {
        ref: messagesWrapperRef,
        className: messagesWrapper,
        children: /*#__PURE__*/jsxs(TransitionGroup, {
          component: null,
          children: [dialogues.isPending && /*#__PURE__*/jsx(SizeTransition, {
            swappable: true,
            axis: "vertical",
            timeout: loadingTransitionDurationMs,
            classNames: messagesContainer,
            getMaxSizeInPx: getTransitionMaxHeight,
            children: ref => /*#__PURE__*/jsx("div", {
              ref: ref,
              className: cx(messagesContainer, loading),
              children: "Indl\xE6ser..."
            })
          }), !dialogues.isPending && /*#__PURE__*/jsx(SizeTransition, {
            swappable: true,
            axis: "vertical",
            timeout: loadingTransitionDurationMs,
            classNames: messagesContainer,
            getMaxSizeInPx: getTransitionMaxHeight,
            children: ref => /*#__PURE__*/jsx("div", {
              ref: ref,
              className: messagesContainer,
              children: sortedDialogues.map(entry => /*#__PURE__*/jsx(DialogueEntry, {
                dialogueId: props.dialogId,
                entry: entry
              }, entry.id))
            })
          })]
        })
      }), /*#__PURE__*/jsx(DialogueInput, {
        dialogueId: props.dialogId,
        messagesContainerRef: messagesWrapperRef
      })]
    })
  });
}
function getTransitionMaxHeight() {
  return {
    height: sizes.dialogMaxHeight - sizes.headerHeight - sizes.footerMinHeight
  };
}

export { DialogOverlay };
//# sourceMappingURL=overlay.dialog.js.map
